import { Injectable } from '@angular/core';
import { FormBuilderService } from './form-builder.service';
import { GlobalServicesService } from './global-services.service';
import { firstValueFrom } from 'rxjs';

export interface FieldLookupConfig {
  fieldName: string;
  lookupType: 'role' | 'institute' | 'location' | 'certification' | 'language' | 'skill' | 'ssc_board' | 'intermediate_board' | 'entrance_test' | 'education_degree' | 'education_branch';
  apiField: string;
  labelKey?: string;
  valueKey?: string;
}

@Injectable({
  providedIn: 'root'
})
export class FieldLookupService {
  private lookupCache = new Map<string, Map<string, string>>();
  private formFieldOptionsCache = new Map<string, Map<string, any>>();
  private loadingStates = new Map<string, boolean>();
  private cacheExpiry = new Map<string, number>();
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  private readonly FIELD_CONFIGS: FieldLookupConfig[] = [
    { fieldName: 'preferred_role', lookupType: 'role', apiField: 'roles', valueKey: 'job_role_id', labelKey: 'job_role_name' },    
    { fieldName: 'ug_college_name', lookupType: 'institute', apiField: 'institutes', valueKey: 'name', labelKey: 'name' },
    { fieldName: 'pg_college_name', lookupType: 'institute', apiField: 'institutes', valueKey: 'name', labelKey: 'name' },
    { fieldName: 'diploma_college_name', lookupType: 'institute', apiField: 'institutes', valueKey: 'name', labelKey: 'name' },    
    { fieldName: 'preferred_location_1', lookupType: 'location', apiField: 'roles', valueKey: 'preferred_location', labelKey: 'preferred_location' },
    { fieldName: 'preferred_location_2', lookupType: 'location', apiField: 'roles', valueKey: 'preferred_location', labelKey: 'preferred_location' },
    { fieldName: 'preferred_location_3', lookupType: 'location', apiField: 'roles', valueKey: 'preferred_location', labelKey: 'preferred_location' },    
    { fieldName: 'certification_1', lookupType: 'certification', apiField: 'certifications', valueKey: 'name', labelKey: 'name' },
    { fieldName: 'certifications', lookupType: 'certification', apiField: 'certifications', valueKey: 'name', labelKey: 'name' },    
    { fieldName: 'known_languages', lookupType: 'language', apiField: 'languages', valueKey: 'name', labelKey: 'name' },
    { fieldName: 'language_proficiency', lookupType: 'language', apiField: 'languages', valueKey: 'name', labelKey: 'name' },    
    { fieldName: 'skill_list', lookupType: 'skill', apiField: 'skills', valueKey: 'name', labelKey: 'name' },
    { fieldName: 'skill_level', lookupType: 'skill', apiField: 'skills', valueKey: 'name', labelKey: 'name' },
    { fieldName: 'skill_achievements', lookupType: 'skill', apiField: 'skills', valueKey: 'name', labelKey: 'name' },    
    { fieldName: 'class10_board_name', lookupType: 'ssc_board', apiField: 'ssc_boards', valueKey: 'name', labelKey: 'name' },
    { fieldName: 'class12_board_name', lookupType: 'intermediate_board', apiField: 'intermediate_boards', valueKey: 'name', labelKey: 'name' },
    { fieldName: 'class10_board_type', lookupType: 'ssc_board', apiField: 'ssc_boards', valueKey: 'name', labelKey: 'name' },
    { fieldName: 'class12_board_type', lookupType: 'intermediate_board', apiField: 'intermediate_boards', valueKey: 'name', labelKey: 'name' },    
    { fieldName: 'entrance_test', lookupType: 'entrance_test', apiField: 'entrance_tests', valueKey: 'name', labelKey: 'name' },
    { fieldName: 'entrance_test_result', lookupType: 'entrance_test', apiField: 'entrance_tests', valueKey: 'name', labelKey: 'name' },
    { fieldName: 'ug_degree', lookupType: 'education_degree', apiField: 'education_degrees', valueKey: 'name', labelKey: 'name' },
    { fieldName: 'pg_degree', lookupType: 'education_degree', apiField: 'education_degrees', valueKey: 'name', labelKey: 'name' },
    { fieldName: 'diploma_degree', lookupType: 'education_degree', apiField: 'education_degrees', valueKey: 'name', labelKey: 'name' },
    { fieldName: 'ug_specialization', lookupType: 'education_branch', apiField: 'education_branches', valueKey: 'name', labelKey: 'name' },
    { fieldName: 'pg_specialization', lookupType: 'education_branch', apiField: 'education_branches', valueKey: 'name', labelKey: 'name' },
    { fieldName: 'diploma_specialization', lookupType: 'education_branch', apiField: 'education_branches', valueKey: 'name', labelKey: 'name' }
  ];

  constructor(
    private formBuilderService: FormBuilderService,
    private globalService: GlobalServicesService
  ) {}
  getLookupValue(fieldName: string, fieldValue: any, eventId: string): any {
    if (!fieldValue || fieldValue === '-') return fieldValue;

    if (Array.isArray(fieldValue)) {
      if (fieldValue.length === 0) return '-';
      
      if (fieldValue.every(item => typeof item === 'string' && item.length === 1)) {
        return this.getSingleLookupValue(fieldName, fieldValue.join(''), eventId);
      }
      
      return fieldValue.map(val => this.getSingleLookupValue(fieldName, val, eventId)).join(', ');
    }

    return this.getSingleLookupValue(fieldName, fieldValue, eventId);
  }
  setFormFieldOptions(eventId: string, fieldOptions: any): void {
    if (!eventId || !fieldOptions) return;
    const fieldOptionsMap = new Map<string, any>();    
    for (const formType in fieldOptions) {
      const fields = fieldOptions[formType];
      if (Array.isArray(fields)) {
        fields.forEach((field: any) => {
          if (field.field_name && field.field_options && Array.isArray(field.field_options)) {
            const optionsMap = new Map<string, string>();
            field.field_options.forEach((option: any) => {
              if (option.value && option.label) {
                optionsMap.set(String(option.value), option.label);
              }
            });
            if (optionsMap.size > 0) {
              fieldOptionsMap.set(field.field_name, optionsMap);
            }
          }
        });
      }
    }
    
    this.formFieldOptionsCache.set(eventId, fieldOptionsMap);
  }
  private getSingleLookupValue(fieldName: string, fieldValue: any, eventId: string): any {
    const formFieldOptionsMap = this.formFieldOptionsCache.get(eventId);
    if (formFieldOptionsMap) {
      const fieldOptions = formFieldOptionsMap.get(fieldName);
      if (fieldOptions) {
        const formFieldValue = fieldOptions.get(String(fieldValue));
        if (formFieldValue) {
          return formFieldValue;
        }
      }
    }
    const config = this.FIELD_CONFIGS.find(c => c.fieldName === fieldName);
    if (!config) return fieldValue;

    const lookupMap = this.lookupCache.get(`${eventId}_${config.lookupType}`);
    if (!lookupMap) return fieldValue;

    return lookupMap.get(String(fieldValue)) || fieldValue;
  }

  async loadLookupData(eventId: string, formDetails?: any, context?: string): Promise<void> {
    if (!eventId) return;
    if (this.isDataLoaded(eventId) && !this.isDataExpired(eventId)) {
      return;
    }
    if (this.loadingStates.get(eventId)) {
      return;
    }

    this.loadingStates.set(eventId, true);

    try {
      const sharedService = this.globalService.getSharedService();      
      const commonData = sharedService.getCommonLookupData();

      if (commonData) {
        await this.processLookupData(eventId, commonData);
      }

      const lookupFlags = this.buildOptimizedLookupFlags(formDetails, commonData);
      
      const response = await firstValueFrom(
        sharedService.initiateFormDetailsRequest(eventId, () => 
          this.formBuilderService.getEventSpecificFormDetailsFastApi(eventId, lookupFlags)
        ) || this.formBuilderService.getEventSpecificFormDetailsFastApi(eventId, lookupFlags)
      );
      const apiData = response?.body || response;        
      
              if (!commonData && apiData) {
          const commonLookupData = this.extractCommonLookupData(apiData);
          if (Object.keys(commonLookupData).length > 0) {
            sharedService.setCommonLookupData(commonLookupData);
          }
        }
      
      await this.processLookupData(eventId, apiData);
      this.cacheExpiry.set(eventId, Date.now() + this.CACHE_DURATION);
    } catch (error) {
      console.error('Error loading lookup data:', error);
    } finally {
      this.loadingStates.set(eventId, false);
    }
  }
  private async processLookupData(eventId: string, data: any): Promise<void> {
    if (data.roles && Array.isArray(data.roles)) {
      const roleMap = new Map<string, string>();
      data.roles.forEach((role: any) => {
        roleMap.set(String(role.job_role_id), role.job_role_name);
      });
      this.lookupCache.set(`${eventId}_role`, roleMap);
    }

    if (data.institutes && Array.isArray(data.institutes)) {
      const instituteMap = new Map<string, string>();
      data.institutes.forEach((institute: any) => {
        instituteMap.set(institute.name, `${institute.name}, ${institute.city}`);
      });
      this.lookupCache.set(`${eventId}_institute`, instituteMap);
    }

    if (data.roles && Array.isArray(data.roles)) {
      const locationMap = new Map<string, string>();
      data.roles.forEach((role: any) => {
        if (role.preferred_location && Array.isArray(role.preferred_location)) {
          role.preferred_location.forEach((location: string) => {
            locationMap.set(location, location);
          });
        }
      });
      this.lookupCache.set(`${eventId}_location`, locationMap);
    }

    if (data.certifications && Array.isArray(data.certifications)) {
      const certMap = new Map<string, string>();
      data.certifications.forEach((cert: any) => {
        certMap.set(cert.name, cert.name);
      });
      this.lookupCache.set(`${eventId}_certification`, certMap);
    }

    if (data.languages && Array.isArray(data.languages)) {
      const languageMap = new Map<string, string>();
      data.languages.forEach((lang: any) => {
        languageMap.set(lang.name, lang.name);
      });
      this.lookupCache.set(`${eventId}_language`, languageMap);
    }

    if (data.skills && Array.isArray(data.skills)) {
      const skillMap = new Map<string, string>();
      data.skills.forEach((skill: any) => {
        skillMap.set(skill.name, skill.name);
      });
      this.lookupCache.set(`${eventId}_skill`, skillMap);
    }

    if (data.ssc_boards && Array.isArray(data.ssc_boards)) {
      const sscBoardMap = new Map<string, string>();
      data.ssc_boards.forEach((board: any) => {
        sscBoardMap.set(board.name, board.name);
      });
      this.lookupCache.set(`${eventId}_ssc_board`, sscBoardMap);
    }
    if (data.intermediate_boards && Array.isArray(data.intermediate_boards)) {
      const interBoardMap = new Map<string, string>();
      data.intermediate_boards.forEach((board: any) => {
        interBoardMap.set(board.name, board.name);
      });
      this.lookupCache.set(`${eventId}_intermediate_board`, interBoardMap);
    }
    if (data.entrance_tests && Array.isArray(data.entrance_tests)) {
      const entranceMap = new Map<string, string>();
      data.entrance_tests.forEach((test: any) => {
        entranceMap.set(test.name, test.name);
      });
      this.lookupCache.set(`${eventId}_entrance_test`, entranceMap);
    }
  }

  private isDataLoaded(eventId: string): boolean {
    return this.lookupCache.has(`${eventId}_role`) || 
           this.lookupCache.has(`${eventId}_institute`) ||
           this.lookupCache.has(`${eventId}_ssc_board`) ||
           this.lookupCache.has(`${eventId}_intermediate_board`);
  }

  private isDataExpired(eventId: string): boolean {
    const expiry = this.cacheExpiry.get(eventId);
    return !expiry || Date.now() > expiry;
  }

  clearLookupData(eventId: string): void {
    const keysToDelete = [];
    for (const key of this.lookupCache.keys()) {
      if (key.startsWith(`${eventId}_`)) {
        keysToDelete.push(key);
      }
    }
    
    keysToDelete.forEach(key => this.lookupCache.delete(key));
    this.formFieldOptionsCache.delete(eventId);
    this.cacheExpiry.delete(eventId);
    this.loadingStates.delete(eventId);
  }

  clearAllLookupData(): void {
    this.lookupCache.clear();
    this.formFieldOptionsCache.clear();
    this.cacheExpiry.clear();
    this.loadingStates.clear();
  }

  getSupportedFields(): string[] {
    return this.FIELD_CONFIGS.map(config => config.fieldName);
  }

  isFieldSupported(fieldName: string): boolean {
    return this.FIELD_CONFIGS.some(config => config.fieldName === fieldName);
  }

  getLookupStats(): any {
    return {
      totalCachedEvents: new Set(Array.from(this.lookupCache.keys()).map(key => key.split('_')[0])).size,
      totalLookupMaps: this.lookupCache.size,
      loadingStates: Object.fromEntries(this.loadingStates),
      cacheExpiry: Object.fromEntries(this.cacheExpiry)
    };
  }
  getOptimizationStats(eventId: string, formDetails?: any): any {
    const flags = formDetails ? this.analyzeFormFieldsForLookupSections(formDetails) : null;
    const requiredSections = flags ? Object.entries(flags).filter(([_, value]) => value).map(([key, _]) => key) : [];
    const totalSections = 11; 
    const optimizationPercentage = flags ? Math.round((requiredSections.length / totalSections) * 100) : 0;

    return {
      eventId,
      requiredSections,
      totalSections,
      optimizationPercentage,
      flags: flags || 'No form details provided',
      estimatedPayloadReduction: `${100 - optimizationPercentage}%`
    };
  }
  analyzeFormFieldsForLookupSections(formDetails: any): {
    includeRoles: boolean;
    includeInstitutes: boolean;
    includeCertifications: boolean;
    includeLanguages: boolean;
    includeSkills: boolean;
    includeSscBoards: boolean;
    includeIntermediateBoards: boolean;
    includeEntranceTests: boolean;
    includeEducationDegrees: boolean;
    includeEducationBranches: boolean;
  } {
    const flags = {
      includeRoles: false,
      includeInstitutes: false,
      includeCertifications: false,
      includeLanguages: false,
      includeSkills: false,
      includeSscBoards: false,
      includeIntermediateBoards: false,
      includeEntranceTests: false,
      includeEducationDegrees: false,
      includeEducationBranches: false
    };

    if (!formDetails || typeof formDetails !== 'object') {
      return flags;
    }
    
    const fieldNames = this.extractFieldNamesFromFormDetails(formDetails);
    fieldNames.forEach(fieldName => {
      const config = this.FIELD_CONFIGS.find(c => c.fieldName === fieldName);
      if (config) {
        switch (config.lookupType) {
          case 'role':
            flags.includeRoles = true;
            break;
          case 'institute':
            flags.includeInstitutes = true;
            break;
          case 'certification':
            flags.includeCertifications = true;
            break;
          case 'language':
            flags.includeLanguages = true;
            break;
          case 'skill':
            flags.includeSkills = true;
            break;
          case 'ssc_board':
            flags.includeSscBoards = true;
            break;
          case 'intermediate_board':
            flags.includeIntermediateBoards = true;
            break;
          case 'entrance_test':
            flags.includeEntranceTests = true;
            break;
          case 'education_degree':
            flags.includeEducationDegrees = true;
            break;
          case 'education_branch':
            flags.includeEducationBranches = true;
            break;
        }
      }
    });

    return flags;
  }

  private extractFieldNamesFromFormDetails(formDetails: any): string[] {
    const fieldNames: string[] = [];

    if (Array.isArray(formDetails)) {
      formDetails.forEach(form => {
        if (form && typeof form === 'object') {
          fieldNames.push(...this.extractFieldNamesFromFormDetails(form));
        }
      });
    } else if (formDetails && typeof formDetails === 'object') {
      Object.values(formDetails).forEach(section => {
        if (Array.isArray(section)) {
          section.forEach(field => {
            if (field && typeof field === 'object' && field.field_name) {
              fieldNames.push(field.field_name);
            }
          });
        } else if (section && typeof section === 'object') {
          fieldNames.push(...this.extractFieldNamesFromFormDetails(section));
        }
      });
    }

    return fieldNames;
  }


  private buildOptimizedLookupFlags(formDetails?: any, commonData?: any): any {
    const flags = formDetails ? this.analyzeFormFieldsForLookupSections(formDetails) : {
      includeRoles: true,
      includeInstitutes: false,
      includeCertifications: false,
      includeLanguages: false,
      includeSkills: false,
      includeSscBoards: false,
      includeIntermediateBoards: false,
      includeEntranceTests: false,
      includeEducationDegrees: false,
      includeEducationBranches: false
    };
    
    flags.includeRoles = true; // Always include roles
    
    if (commonData) {
      flags.includeInstitutes = false;
      flags.includeCertifications = false;
      flags.includeLanguages = false;
      flags.includeSkills = false;
      flags.includeSscBoards = false;
      flags.includeIntermediateBoards = false;
      flags.includeEntranceTests = false;
      flags.includeEducationDegrees = false;
      flags.includeEducationBranches = false;
    }

    return flags;
  }

  private extractCommonLookupData(data: any): any {
    const commonData: any = {};
    
    if (data.institutes) commonData.institutes = data.institutes;
    if (data.certifications) commonData.certifications = data.certifications;
    if (data.languages) commonData.languages = data.languages;
    if (data.skills) commonData.skills = data.skills;
    if (data.ssc_boards) commonData.ssc_boards = data.ssc_boards;
    if (data.intermediate_boards) commonData.intermediate_boards = data.intermediate_boards;
    if (data.entrance_tests) commonData.entrance_tests = data.entrance_tests;
    if (data.education_degrees) commonData.education_degrees = data.education_degrees;
    if (data.education_branches) commonData.education_branches = data.education_branches;
    
    return commonData;
  }

  // Common method for all components - analyzes form fields and makes optimized API calls
  async loadOptimizedLookupData(eventId: string, formFields: any[], context?: string): Promise<any> {
    const sharedService = this.globalService.getSharedService();
    const commonData = sharedService.getCommonLookupData();
    
    // Analyze form fields to determine what's needed
    const fieldNames = this.extractFieldNamesFromFormFields(formFields);
    
    // Check what's available in shared service cache
    const hasInstitutes = commonData?.institutes && commonData.institutes.length > 0;
    const hasCertifications = commonData?.certifications && commonData.certifications.length > 0;
    const hasLanguages = commonData?.languages && commonData.languages.length > 0;
    const hasSkills = commonData?.skills && commonData.skills.length > 0;
    const hasSscBoards = commonData?.ssc_boards && commonData.ssc_boards.length > 0;
    const hasIntermediateBoards = commonData?.intermediate_boards && commonData.intermediate_boards.length > 0;
    const hasEntranceTests = commonData?.entrance_tests && commonData.entrance_tests.length > 0;
    const hasEducationDegrees = commonData?.education_degrees && commonData.education_degrees.length > 0;
    const hasEducationBranches = commonData?.education_branches && commonData.education_branches.length > 0;
    
    // Build flags: only request what's needed AND not already cached
    const lookupFlags = {
      includeRoles: true, // Always request roles (event-specific)
      includeInstitutes: this.checkFieldForLookup(fieldNames, 'institute') && !hasInstitutes,
      includeCertifications: this.checkFieldForLookup(fieldNames, 'certification') && !hasCertifications,
      includeLanguages: this.checkFieldForLookup(fieldNames, 'language') && !hasLanguages,
      includeSkills: this.checkFieldForLookup(fieldNames, 'skill') && !hasSkills,
      includeSscBoards: this.checkFieldForLookup(fieldNames, 'ssc_board') && !hasSscBoards,
      includeIntermediateBoards: this.checkFieldForLookup(fieldNames, 'intermediate_board') && !hasIntermediateBoards,
      includeEntranceTests: this.checkFieldForLookup(fieldNames, 'entrance_test') && !hasEntranceTests,
      includeEducationDegrees: this.checkFieldForLookup(fieldNames, 'education_degree') && !hasEducationDegrees,
      includeEducationBranches: this.checkFieldForLookup(fieldNames, 'education_branch') && !hasEducationBranches
    };
    
    // Make API call only if we need any data
    const needsApiCall = Object.values(lookupFlags).some(flag => flag === true);
    
    if (needsApiCall) {
      const response = await this.formBuilderService.getEventSpecificFormDetailsFastApi(eventId, lookupFlags, context).toPromise();
      const apiData = response?.body || response;
      
      // Extract and cache common data if not already cached
      if (!commonData && apiData) {
        const extractedCommonData = this.extractCommonLookupData(apiData);
        if (Object.keys(extractedCommonData).length > 0) {
          sharedService.setCommonLookupData(extractedCommonData);
        }
      }
      
      // Return combined data (cached + API)
      return {
        roles: apiData?.roles || [],
        institutes: commonData?.institutes || apiData?.institutes || [],
        certifications: commonData?.certifications || apiData?.certifications || [],
        languages: commonData?.languages || apiData?.languages || [],
        skills: commonData?.skills || apiData?.skills || [],
        ssc_boards: commonData?.ssc_boards || apiData?.ssc_boards || [],
        intermediate_boards: commonData?.intermediate_boards || apiData?.intermediate_boards || [],
        entrance_tests: commonData?.entrance_tests || apiData?.entrance_tests || [],
        education_degrees: commonData?.education_degrees?.length
          ? commonData.education_degrees : (apiData?.education_degrees || []),
        education_branches: commonData?.education_branches?.length
          ? commonData.education_branches : (apiData?.education_branches || []),
        proctoring_config: apiData?.proctoring_config ?? { enabled: false }
      };
    } else {
      const rolesResponse = await this.formBuilderService.getEventSpecificFormDetailsFastApi(eventId, {
        includeRoles: true,
        includeInstitutes: false,
        includeCertifications: false,
        includeLanguages: false,
        includeSkills: false,
        includeSscBoards: false,
        includeIntermediateBoards: false,
        includeEntranceTests: false,
        includeEducationDegrees: false,
        includeEducationBranches: false
      }, context).toPromise();
      
      const rolesData = rolesResponse?.body || rolesResponse;
      
      return {
        roles: rolesData?.roles || [],
        institutes: commonData?.institutes || [],
        certifications: commonData?.certifications || [],
        languages: commonData?.languages || [],
        skills: commonData?.skills || [],
        ssc_boards: commonData?.ssc_boards || [],
        intermediate_boards: commonData?.intermediate_boards || [],
        entrance_tests: commonData?.entrance_tests || [],
        education_degrees: commonData?.education_degrees || [],
        education_branches: commonData?.education_branches || [],
        proctoring_config: rolesData?.proctoring_config ?? { enabled: false }
      };
    }
  }

  buildLookupFlagsFromFormFields(formFields: any[]): any {
    const sharedService = this.globalService.getSharedService();
    const commonData = sharedService.getCommonLookupData();
    
    // Always analyze form fields to see what's actually needed
    const fieldNames = this.extractFieldNamesFromFormFields(formFields);
    
    // Check what's available in shared service cache
    const hasInstitutes = commonData?.institutes && commonData.institutes.length > 0;
    const hasCertifications = commonData?.certifications && commonData.certifications.length > 0;
    const hasLanguages = commonData?.languages && commonData.languages.length > 0;
    const hasSkills = commonData?.skills && commonData.skills.length > 0;
    const hasSscBoards = commonData?.ssc_boards && commonData.ssc_boards.length > 0;
    const hasIntermediateBoards = commonData?.intermediate_boards && commonData.intermediate_boards.length > 0;
    const hasEntranceTests = commonData?.entrance_tests && commonData.entrance_tests.length > 0;
    const hasEducationDegrees = commonData?.education_degrees && commonData.education_degrees.length > 0;
    const hasEducationBranches = commonData?.education_branches && commonData.education_branches.length > 0;
    
    // Build flags: only request what's needed AND not already cached
    return {
      includeRoles: true, // Always request roles (event-specific)
      includeInstitutes: this.checkFieldForLookup(fieldNames, 'institute') && !hasInstitutes,
      includeCertifications: this.checkFieldForLookup(fieldNames, 'certification') && !hasCertifications,
      includeLanguages: this.checkFieldForLookup(fieldNames, 'language') && !hasLanguages,
      includeSkills: this.checkFieldForLookup(fieldNames, 'skill') && !hasSkills,
      includeSscBoards: this.checkFieldForLookup(fieldNames, 'ssc_board') && !hasSscBoards,
      includeIntermediateBoards: this.checkFieldForLookup(fieldNames, 'intermediate_board') && !hasIntermediateBoards,
      includeEntranceTests: this.checkFieldForLookup(fieldNames, 'entrance_test') && !hasEntranceTests,
      includeEducationDegrees: this.checkFieldForLookup(fieldNames, 'education_degree') && !hasEducationDegrees,
      includeEducationBranches: this.checkFieldForLookup(fieldNames, 'education_branch') && !hasEducationBranches
    };
  }

  private extractFieldNamesFromFormFields(formFields: any[]): string[] {
    const fieldNames: string[] = [];
    
    formFields.forEach((section: any) => {
      if (section.section_form_fields) {
        section.section_form_fields.forEach((field: any) => {
          if (field.element_type === 'field') {
            fieldNames.push(field.name);
          } else if (field.element_type === 'group' && field.form_fields) {
            field.form_fields.forEach((groupField: any) => {
              fieldNames.push(groupField.name);
            });
          }
        });
      } else if (section.field_name) {
        // Handle direct field objects (from workflow ruleset)
        fieldNames.push(section.field_name);
      }
    });

    return fieldNames;
  }

  private checkFieldForLookup(fieldNames: string[], lookupType: string): boolean {
    // Degree and branch are matched exactly rather than by substring: "ug_degree" is a prefix
    // of "ug_degree_type" (the study mode), so a substring check would pull the degree master
    // list for a form that only carries Full time / Part time.
    if (lookupType === 'education_degree' || lookupType === 'education_branch') {
      const normalised = fieldNames.map(name => String(name ?? '').toLowerCase());
      return this.FIELD_CONFIGS.some(config =>
        config.lookupType === lookupType && normalised.includes(config.fieldName.toLowerCase())
      );
    }

    const lookupMappings: { [key: string]: string[] } = {
      'role': ['preferred_role', 'job_role', 'role'],
      'institute': ['college_name', 'institute', 'ug_college_name', 'pg_college_name', 'diploma_college_name'],
      'certification': ['certifications', 'certification'],
      'language': ['known_languages', 'language'],
      'skill': ['skill_list', 'skill', 'skills'],
      'ssc_board': ['class10_board_name', 'ssc_board'],
      'intermediate_board': ['class12_board_name', 'intermediate_board'],
      'entrance_test': ['entrance_test']
    };

    const patterns = lookupMappings[lookupType] || [];
    return patterns.some(pattern => 
      fieldNames.some(fieldName => 
        fieldName.toLowerCase().includes(pattern.toLowerCase())
      )
    );
  }
}
